import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ShoppingCart, Banknote, CreditCard, TrendingUp, Activity } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { fmtNaira } from "@/lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    adminApi.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((err) => {
        const status = err?.response?.status;
        // 401/403 already handled by the adminApi interceptor (it redirects).
        // For everything else, surface a friendly error instead of the
        // eternal loading spinner.
        if (status && status !== 401 && status !== 403) {
          setError(err?.response?.data?.detail || "Failed to load dashboard.");
        }
      });
  }, [nav]);

  if (error) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center" data-testid="admin-dashboard-error">
        <div className="text-lg font-heading font-bold text-red-700">Couldn't load dashboard</div>
        <p className="text-sm text-kudi-ink/60 mt-2">{error}</p>
        <button onClick={() => window.location.reload()} className="mt-4 px-3 py-2 bg-kudi-green text-white rounded-lg text-sm font-semibold">
          Try again
        </button>
      </div>
    );
  }
  if (!stats) return <div className="p-8 text-kudi-ink/50" data-testid="admin-dashboard-loading">Loading...</div>;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold">Overview</h1>
        <p className="text-sm text-kudi-ink/60">Real-time metrics across Kobolog AI</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPI icon={Users} label="Total traders" value={stats.users.total} hint={`${stats.users.signups_24h} in last 24h`} color="text-emerald-700" bg="bg-emerald-50" testid="kpi-users" />
        <KPI icon={ShoppingCart} label="Onboarded" value={stats.users.onboarded} hint={`${Math.round((stats.users.onboarded / Math.max(stats.users.total, 1)) * 100)}% completion`} color="text-kudi-green" bg="bg-accent" testid="kpi-onboarded" />
        <KPI icon={Activity} label="Transactions" value={stats.transactions.total} hint={fmtNaira(stats.transactions.sales_value) + " in sales"} color="text-blue-700" bg="bg-blue-50" testid="kpi-txs" />
        <KPI icon={CreditCard} label="Payments" value={stats.payments.successful} hint={fmtNaira(stats.payments.total_value)} color="text-amber-700" bg="bg-amber-50" testid="kpi-payments" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Banknote className="w-4 h-4 text-kudi-terracotta" />
            <h3 className="font-heading font-semibold">Loan book</h3>
          </div>
          <div className="space-y-2 text-sm">
            <Row label="Total loans" value={stats.loans.total} />
            <Row label="Active" value={stats.loans.active} />
            <Row label="Disbursed" value={fmtNaira(stats.loans.disbursed)} />
            <Row label="Repaid" value={fmtNaira(stats.loans.repaid)} />
            <Row label="Outstanding" value={fmtNaira(stats.loans.disbursed - stats.loans.repaid)} bold />
          </div>
        </div>
        <div className="bg-gradient-to-br from-kudi-green to-kudi-green-dark rounded-2xl p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 ankara-watermark opacity-10" aria-hidden />
          <div className="relative">
            <div className="text-xs uppercase tracking-wider text-white/75 font-semibold">Total ₦ moved</div>
            <div className="font-heading text-4xl font-bold tabular-nums mt-2">
              {fmtNaira(stats.transactions.sales_value + stats.payments.total_value + stats.loans.disbursed)}
            </div>
            <div className="text-sm text-white/75 mt-1">All-time across sales, payments & loans</div>
            <div className="mt-4 flex items-center gap-2 text-xs text-white/85">
              <TrendingUp className="w-3.5 h-3.5" />
              {stats.users.signups_24h > 0 ? `+${stats.users.signups_24h} signup${stats.users.signups_24h > 1 ? "s" : ""} today` : "Awaiting today's growth"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, hint, color, bg, testid }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg ${bg} ${color} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs text-kudi-ink/55">{label}</div>
      <div className="font-heading text-2xl font-bold tabular-nums mt-0.5">{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div className="text-xs text-kudi-ink/45 mt-0.5 truncate">{hint}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-kudi-ink/65">{label}</span>
      <span className={`tabular-nums ${bold ? "font-heading font-bold" : "font-medium"}`}>{value}</span>
    </div>
  );
}
